// Document shapes, as stored in Firestore. See "Data model and security
// rules" in the build plan. A shared task and a private task have the same
// shape; only the path differs.

import type { Timestamp } from 'firebase/firestore'

/** One-off, repeating on a schedule, or a routine done once a week on whichever day it is put. */
export type TaskKind = 'once' | 'repeat' | 'routine'

export const TASK_KINDS: TaskKind[] = ['once', 'repeat', 'routine']

export type PaletteKey = 'c1' | 'c2' | 'c3' | 'c4' | 'c5' | 'c6' | 'c7' | 'c8'

export const PALETTE_KEYS: PaletteKey[] = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8']

/** A calendar date as `YYYY-MM-DD`. Compared as a string, never through UTC. */
export type DateStr = string

/** A clock time as `HH:mm`, 24-hour. */
export type TimeStr = string

export type Scope = 'shared' | 'private'

export interface ChecklistStep {
  id: string
  text: string
  done: boolean
}

export type RecurrenceFreq = 'day' | 'week' | 'month' | 'year'

export type RecurrenceBasis = 'schedule' | 'completion'

export interface Recurrence {
  freq: RecurrenceFreq
  interval: number
  /** ISO weekdays, 1 = Monday to 7 = Sunday; weekly rules only. */
  weekdays: number[] | null
  /** Day of month, 1 to 31, clamped to the month's last day; monthly rules only. */
  monthDay: number | null
  basis: RecurrenceBasis
  /** Where the pattern puts the current occurrence, apart from the task's own due date. */
  scheduledDate: DateStr | null
  /** The usual due time, restored when an occurrence is moved for this one only. */
  scheduledTime: TimeStr | null
}

/**
 * What ticking moved on, so unticking can put it back: the occurrence's dates
 * and time, where the pattern had it, and each step's tick (by step id).
 */
export interface CompletionBefore {
  kind: 'repeat' | 'routine'
  dueDate: DateStr | null
  dueTime: TimeStr | null
  plannedDate: DateStr | null
  weekOf: DateStr | null
  scheduledDate: DateStr | null
  scheduledTime: TimeStr | null
  steps: { id: string; done: boolean }[]
}

/** One ticked occurrence of a repeat or a routine. Kept for good: past weeks are read from them. */
export interface Completion {
  /** The occurrence: a repeat's due date, or the Monday of a routine's week. */
  date: DateStr
  /** The local day it was ticked. */
  doneOn: DateStr
  completedAt: Timestamp
  /** Missing on ticks made before 2.5.0. */
  before?: CompletionBefore
}

export interface Reminder {
  /** Minutes before the due time. */
  offsetMin: number
}

export interface TaskDoc {
  title: string
  description: string | null
  kind: TaskKind
  /** Manual position within a day or a tray group. Fractional. */
  order: number
  categoryId: string | null
  /** Null means either of us. Absent on private tasks. */
  assigneeUid?: string | null
  checklist: ChecklistStep[]
  /** "Do it on": where the task sits. A routine's day in its current week. Never a time. */
  plannedDate: DateStr | null
  /** The deadline: overdue, reminders and the morning summary. Required on a repeat, never on a routine. */
  dueDate: DateStr | null
  /** Only with a due date. Null means all day. */
  dueTime: TimeStr | null
  /** Repeats and routines stay open; their ticks go to `completions`. */
  done: boolean
  /** The local day a one-off was ticked. */
  doneOn: DateStr | null
  completedAt: Timestamp | null
  /** Waiting on someone; the task sits on its follow-up day while it has one. */
  waiting: boolean
  followUpDate: DateStr | null
  followUpTime: TimeStr | null
  /** Repeats only. */
  recurrence: Recurrence | null
  /** Routines only: the Monday of the week the current occurrence belongs to. */
  weekOf: DateStr | null
  completions: Completion[]
  reminder: Reminder | null
  remindAt: Timestamp | null
  /** Written by the reminder function; equals remindAt once sent. */
  reminderSentFor?: Timestamp | null
  createdBy: string
  updatedBy: string
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface Task extends TaskDoc {
  id: string
  scope: Scope
  /** True while a local write has not reached the server. */
  pending: boolean
}

export interface CategoryDoc {
  name: string
  color: PaletteKey
  order: number
  archived: boolean
  createdBy: string
  updatedBy: string
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface Category extends CategoryDoc {
  id: string
  scope: Scope
}

export interface Member {
  name: string
}

export interface HouseholdDoc {
  name: string
  ownerUid: string
  /** At most two lowercase addresses. */
  memberEmails: string[]
  members: Record<string, Member>
  /** IANA name, used by Functions for the morning summary. */
  timeZone: string
  createdAt: Timestamp
  updatedAt: Timestamp
  updatedBy: string
}

export type CalendarOwner = 'me' | 'other' | 'both'

export type ThemeSetting = 'system' | 'light' | 'dark'

export interface Settings {
  /** Calendar id to whether it is shown. */
  shownCalendars: Record<string, boolean>
  /** Calendar id to whose it is. */
  calendarOwners: Record<string, CalendarOwner>
  defaultVisibility: Scope
  /** Who's doing a new shared task: the person adding it, or either of us. Absent on older accounts, which means either. */
  defaultAssignee?: 'either' | 'me'
  /** Confirm before a private task becomes shared. Absent on older accounts, which means ask. */
  askBeforeSharing?: boolean
  theme: ThemeSetting
  notifications: {
    reminders: boolean
    summary: boolean
    partner: boolean
  }
  summaryTime: TimeStr
}

export interface UserDoc {
  householdId: string | null
  settings: Settings
  /** Written by the summary function. */
  lastSummaryDate: DateStr | null
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface DeviceDoc {
  token: string
  userAgent: string
  updatedAt: Timestamp
}
